import { useState, useEffect, useRef } from 'react';
import { gradeApi } from '../../../api.js';
import { AI_LEARNING_POLLING_INTERVAL, AI_LEARNING_MAX_POLLING_TIME } from './constants.js';
import toast from 'react-hot-toast';

const useAiLearning = (assignment_id, user, getQuestions, getAssignmentSubmission) => {


  // Track AI learning polling
  const learningPollingTimers = useRef({});

  // State for AI learning progress
  const [aiLearningInProgress, setAiLearningInProgress] = useState({});

  // Function to start polling for AI learning completion
  const startPollingForLearningCompletion = subquestionId => {
    // Clear any existing timer first to prevent duplicates
    // Prevent starting multiple timers for the same subquestion
    if (learningPollingTimers.current[subquestionId]) {
      return;
    }

    // Set learning in progress state
    setAiLearningInProgress(prev => ({
      ...prev,
      [subquestionId]: true
    }));

    let pollingCount = 0;
    const startTime = Date.now();

    console.log(`Starting polling for AI learning completion of subquestion ${subquestionId} every ${AI_LEARNING_POLLING_INTERVAL}ms`);

    const checkLearningStatus = async () => {
      console.log(`[${new Date().toLocaleTimeString()}] Checking AI learning status for subquestion ${subquestionId}`);
      try {
        // Check the AI learning status endpoint
        const response = await gradeApi.get({
          path: `/api/assignment/${assignment_id}/subquestion/${subquestionId}/ai-learning-status/`,
          config: {
            headers: {
              Authorization: `Bearer ${user?.session_token}`
            }
          }
        });

        const { is_in_progress } = response.data;

        if (!is_in_progress) {
          console.log(`AI learning for subquestion ${subquestionId} completed, refreshing all data...`);

          // Clear the polling timer first
          clearInterval(learningPollingTimers.current[subquestionId]);
          delete learningPollingTimers.current[subquestionId];

          // Clear learning in progress state
          setAiLearningInProgress(prev => ({
            ...prev,
            [subquestionId]: false
          }));

          // Refresh all data
          if (getQuestions) {
            await getQuestions();
          }

          if (getAssignmentSubmission) {
            await getAssignmentSubmission();
          }

          // Show success message
          toast.success('AI learning completed! Grading has been refined and submissions regraded.');
        } else if (Date.now() - startTime > AI_LEARNING_MAX_POLLING_TIME) {
          // Stop polling if maximum time reached
          console.log(`Reached maximum polling time for AI learning of subquestion ${subquestionId}`);
          clearInterval(learningPollingTimers.current[subquestionId]);
          delete learningPollingTimers.current[subquestionId];

          // Clear learning in progress state
          setAiLearningInProgress(prev => ({
            ...prev,
            [subquestionId]: false
          }));

          // Show timeout message
          toast.error(
            'AI learning is taking longer than expected. Please refresh the page to check the status.'
          );
        }

        pollingCount++;
      } catch (error) {
        console.error('Error checking AI learning status:', error);

        // Stop polling immediately on network errors (like ERR_INSUFFICIENT_RESOURCES)
        if (error.code === 'ERR_NETWORK' || error.code === 'ERR_INSUFFICIENT_RESOURCES') {
          clearInterval(learningPollingTimers.current[subquestionId]);
          delete learningPollingTimers.current[subquestionId];

          // Clear learning in progress state
          setAiLearningInProgress(prev => ({
            ...prev,
            [subquestionId]: false
          }));

          toast.error('Network error while checking AI learning status. Please refresh the page.');
          return;
        }

        // On other errors, stop polling after a few retries
        if (pollingCount > 3) {
          clearInterval(learningPollingTimers.current[subquestionId]);
          delete learningPollingTimers.current[subquestionId];

          // Clear learning in progress state
          setAiLearningInProgress(prev => ({
            ...prev,
            [subquestionId]: false
          }));

          toast.error('Error checking AI learning status. Please refresh the page.');
        }
      }
    };

    // Start polling immediately
    checkLearningStatus();

    // Then set up interval for subsequent checks
    learningPollingTimers.current[subquestionId] = setInterval(
      checkLearningStatus,
      AI_LEARNING_POLLING_INTERVAL
    );
  };

  // Function to trigger AI learning and start polling
  const triggerAiLearningWithPolling = async (subQuestionId) => {
    try {
      await gradeApi.post({
        path: `/api/assignment/${assignment_id}/subquestion/${subQuestionId}/ai-learn-and-regrade/`,
        config: {
          headers: {
            Authorization: `Bearer ${user.session_token}`
          }
        }
      });

      // Start polling for completion
      startPollingForLearningCompletion(subQuestionId);
    } catch (error) {
      console.error('Error triggering AI learning:', error);
      throw error; // Re-throw to be handled by the calling component
    }
  };

  // Helper function to check existing learning processes from questions data
  const checkExistingLearningFromQuestions = (questionsData) => {
    if (!questionsData) {return;}

    for (const question of questionsData) {
      for (const subQuestion of question.subQuestions || []) {
        if (subQuestion.ai_learning_in_progress && !learningPollingTimers.current[subQuestion.id]) {
          console.log(`Found AI learning in progress for subquestion ${subQuestion.id} on page load`);
          // Start polling for this subquestion
          startPollingForLearningCompletion(subQuestion.id);
        }
      }
    }
  };

  // Clean up polling timers on component unmount
  useEffect(() => {
    return () => {
      // Clear all learning polling timers
      Object.values(learningPollingTimers.current).forEach(timer => {
        clearInterval(timer);
      });
    };
  }, []);

  return {
    aiLearningInProgress,
    startPollingForLearningCompletion,
    triggerAiLearningWithPolling,
    checkExistingLearningFromQuestions
  };
};

export default useAiLearning;